/* istanbul ignore file */
import { useCallback, useEffect, useRef } from 'react'

export const useModalFocusTrap = ({ isOpen }: { isOpen: boolean }) => {
  const modalRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      e.stopPropagation()
      return
    }
  }, [])

  const handleFocus = useCallback((e: FocusEvent) => {
    if (isOutsideClick(e)) {
      e.preventDefault()
      modalRef.current?.focus()
    }
  }, [])

  const isOutsideClick = useCallback((e: FocusEvent) => {
    const target = e.target as HTMLElement
    const modal = modalRef.current

    return modal && !modal.contains(target)
  }, [])

  const blockScroll = useCallback(() => {
    document.body.style.overflow = 'hidden'
  }, [])

  useEffect(() => {
    if (!isOpen) return

    const originalOverflow = document.body.style.overflow
    blockScroll()

    document.addEventListener('keydown', handleKeyDown, true)
    document.addEventListener('focusin', handleFocus, true)

    if (modalRef.current) {
      modalRef.current.focus()
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      document.removeEventListener('focusin', handleFocus, true)

      document.body.style.overflow = originalOverflow
    }
  }, [isOpen])

  return modalRef
}
